export const DEALS_DEFAULT_REVALIDATE_AFTER_MS = 2 * 60 * 1000;

/**
 * DealLens deal status values (matches backend DealLensStatus).
 * Use these instead of hardcoded strings when comparing or setting deal status.
 */
export const DealLensStatus = Object.freeze({
  OPEN: "OPEN",
  MESSAGE: "MESSAGE",
  DRAFT: "DRAFT",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
  COUNTERED: "COUNTERED",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED",
  WITHDRAWN: "WITHDRAWN",
});

/**
 * DealLens message_status values (matches backend DealLensMessageStatus).
 * AppSync forwards omitted GraphQL args as null; ENTITY_SCHEMA rejects null enums.
 */
export const DealLensMessageStatus = Object.freeze({
  PENDING_SEND: "PENDING_SEND",
  SENDING: "SENDING",
  SENT: "SENT",
  READ: "READ",
  FAILED: "FAILED",
  DNC: "DNC",
});

/** Shown when Accept Offer is disabled because the receiver changed calculator inputs (HAI-629). */
export const ACCEPT_OFFER_BLOCKED_BY_EDITS_TOOLTIP = "You have modified the offer. Use Counter to submit revised terms.";

const VALID_DEAL_LENS_STATUSES = new Set(Object.values(DealLensStatus));

/**
 * Normalizes a raw status string to a canonical DealLensStatus value.
 *
 * @param {string|undefined|null} raw - Raw status from API or deal object
 * @returns {string} One of DealLensStatus values; defaults to OPEN when invalid or empty
 */
export function normalizeDealLensStatus(raw) {
  const normalized = String(raw ?? "")
    .toUpperCase()
    .trim();
  return VALID_DEAL_LENS_STATUSES.has(normalized) ? normalized : DealLensStatus.OPEN;
}

/** Backend swipe status values for deallensSwipe mutation (use these when calling the API). */
export const DEALLENS_SWIPE_STATUS = {
  LIKED: "LIKED",
  PASSED: "PASSED",
  UNSWIPED: "UNSWIPED",
};

/** Display buckets for grouping deals (derived from backend status via normalizeSwipeBucket). */
export const DEAL_SWIPE_BUCKET = {
  LIKED: "liked",
  PASSED: "passed",
  OTHER: "other",
};

const LIKED_STATUSES = new Set(["like", "liked", "right", "explore", "approved", "accept"]);
const PASSED_STATUSES = new Set(["pass", "passed", "left", "reject", "rejected", "dislike"]);

/**
 * Normalizes a swipe status string into a stable local bucket.
 *
 * @param {string|undefined|null} rawStatus - Raw swipe status from DealLens payload
 * @returns {string} One of DEAL_SWIPE_BUCKET values
 */
export function normalizeSwipeBucket(rawStatus) {
  const normalized = String(rawStatus || "")
    .trim()
    .toLowerCase();
  if (LIKED_STATUSES.has(normalized)) return DEAL_SWIPE_BUCKET.LIKED;
  if (PASSED_STATUSES.has(normalized)) return DEAL_SWIPE_BUCKET.PASSED;
  return DEAL_SWIPE_BUCKET.OTHER;
}

/**
 * Copies list-row fields onto a getDealLens detail row when the target is missing them.
 * Used so swipe_status, collection_id, and property_id survive detail loads and share writes.
 *
 * @param {object | null | undefined} targetDeal
 * @param {object | null | undefined} sourceDeal
 * @returns {object | null | undefined}
 */
export function mergeDealLensSwipeStatus(targetDeal, sourceDeal) {
  if (!targetDeal || typeof targetDeal !== "object") return targetDeal;

  let merged = targetDeal;
  if (!String(merged.swipe_status ?? "").trim()) {
    const inheritedSwipe = String(sourceDeal?.swipe_status ?? "").trim();
    if (inheritedSwipe) merged = { ...merged, swipe_status: inheritedSwipe };
  }
  if (!String(merged.collection_id ?? "").trim()) {
    const inheritedCollection = String(sourceDeal?.collection_id ?? "").trim();
    if (inheritedCollection) merged = { ...merged, collection_id: inheritedCollection };
  }
  // Server tip rows sometimes omit property_id; keep prior so PropertyOfferCache.patchFromTail can match.
  if (!String(merged.property_id ?? "").trim()) {
    const inheritedProperty = String(sourceDeal?.property_id ?? "").trim();
    if (inheritedProperty) merged = { ...merged, property_id: inheritedProperty };
  }
  return merged;
}
